import { Injectable, NgZone } from '@angular/core';
import { Subject, Subscription, fromEvent } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import * as echarts from 'echarts';

@Injectable({ providedIn: 'root' })
export class ResponsiveLayoutService {
  private reflowSubject = new Subject<void>();
  reflow$ = this.reflowSubject.asObservable();
  private resizeSub?: Subscription;
  private initialized = false;
  private rowCount = 0;
  private _screenFloating = true;

  // Dashboard-level auto-resize + responsive grid for ECharts instances
  private chartResizeObserver?: ResizeObserver;
  private chartMutationObserver?: MutationObserver;
  private observedElements = new WeakSet<Element>();
  private gridAppliedElements = new WeakSet<Element>();
  private gridScanTimer?: ReturnType<typeof setInterval>;

  // Track font scale applied to each chart element to avoid compounding
  private fontScaleMap = new Map<Element, number>();
  // Track a representative post-scaling font size per element to detect option resets
  private fontFingerprints = new Map<Element, number>();
  private lastEffectiveScale = 1;

  get screenFloating(): boolean {
    return this._screenFloating;
  }

  /**
   * Effective screen floating state — accounts for screen size override.
   * Small screens always get scroll mode (true) even if config says false.
   */
  get effectiveScreenFloating(): boolean {
    const screen = this.getScreenCategory();
    if (screen === 'small' || screen === 'medium') return true;
    return this._screenFloating;
  }

  constructor(private ngZone: NgZone) {}

  init(): void {
    // Always re-apply DPR zoom — handles HMR / hot-reload where
    // index.html inline script doesn't re-execute
    this.ensureDprZoom();

    if (this.initialized) return;
    this.initialized = true;

    this.updateCSSVariables();

    this.ngZone.runOutsideAngular(() => {
      this.resizeSub = fromEvent(window, 'resize')
        .pipe(debounceTime(150))
        .subscribe(() => {
          this.updateCSSVariables();
          this.ngZone.run(() => this.reflowSubject.next());
        });
    });
  }

  setRowCount(count: number): void {
    this.rowCount = count;
    this.updateCSSVariables();
    this.reflowSubject.next();
  }

  setScreenFloating(value: boolean): void {
    this._screenFloating = value;
    // updateCSSVariables handles dvm-no-scroll class based on screen size
    // (small screens always get scroll mode even if config says no-scroll)
    this.updateCSSVariables();
    // Reset scroll position when switching to no-scroll
    if (!value && document.documentElement.classList.contains('dvm-no-scroll')) {
      const sidenavContent = document.querySelector('mat-sidenav-content, .mat-sidenav-content');
      if (sidenavContent) {
        sidenavContent.scrollTop = 0;
      }
    }
    this.reflowSubject.next();
  }

  triggerReflow(): void {
    this.updateCSSVariables();
    this.reflowSubject.next();
  }

  /**
   * Re-apply the DPR zoom bypass if it should be active but isn't.
   * Handles HMR / hot-reload where index.html inline script doesn't re-execute,
   * so developers don't need to restart ng serve after code changes.
   */
  /**
   * Target DPR for the zoom bypass.
   * Ultra-high-res screens (physical ≥ 3000px, DPR ≥ 2) bypass to 150%.
   * Normal screens bypass to 100%.
   */
  private getTargetDpr(): number {
    const dpr = window.devicePixelRatio || 1;
    const physicalWidth = Math.round((window.screen.width || 0) * dpr);
    if (physicalWidth >= 3000 && dpr >= 2) return 1.5;
    return 1;
  }

  private ensureDprZoom(): void {
    const dpr = window.devicePixelRatio || 1;
    const isLargeScreen = window.screen.width >= 1024;
    const shouldBeActive = dpr !== 1 && isLargeScreen;
    const currentlyActive = this.isDprZoomActive();

    if (shouldBeActive && !currentlyActive) {
      const targetDpr = this.getTargetDpr();
      const z = targetDpr / dpr;
      const s = (dpr / targetDpr) * 100;
      let tag = document.getElementById('dvm-dpr-zoom') as HTMLStyleElement;
      if (!tag) {
        tag = document.createElement('style');
        tag.id = 'dvm-dpr-zoom';
        document.head.appendChild(tag);
      }
      tag.textContent =
        'html{height:100%;overflow:hidden}' +
        'body{transform:scale(' + z + ');transform-origin:0 0;width:' + s + 'vw;height:' + s + 'vh;overflow:hidden}' +
        ':root{--dvh:' + s + 'vh;--dvm-dpr-active:1;--dvm-target-dpr:' + targetDpr + '}';
    } else if (!shouldBeActive && currentlyActive) {
      const tag = document.getElementById('dvm-dpr-zoom');
      if (tag) tag.remove();
      document.documentElement.style.setProperty('--dvm-dpr-active', '0');
    }
  }

  /**
   * Detect if DPR zoom bypass is active (desktop with OS scaling).
   * index.html sets --dvm-dpr-active:1 when zoom is applied.
   */
  private isDprZoomActive(): boolean {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--dvm-dpr-active')?.trim() === '1';
  }

  /**
   * Get the effective viewport height in CSS pixels.
   * When DPR zoom is active, body is dpr*100vh, so multiply innerHeight by dpr.
   * When not active (mobile/tablet or dpr=1), use innerHeight directly.
   */
  private getEffectiveVh(): number {
    if (this.isDprZoomActive()) {
      const dpr = window.devicePixelRatio || 1;
      const targetDpr = this.getTargetDpr();
      // body zoom = targetDpr/dpr, so effective height = innerHeight * dpr/targetDpr
      return Math.round(window.innerHeight * dpr / targetDpr);
    }
    return window.innerHeight;
  }

  /**
   * Screen size category for adaptive overheads and minimums.
   */
  private getScreenCategory(): 'small' | 'medium' | 'large' {
    const width = window.innerWidth;
    if (width < 768) return 'small';       // Mobile
    if (width < 1024) return 'medium';     // Tablet
    return 'large';                         // Laptop / Monitor
  }

  /**
   * Compute a font scale factor for large/TV screens.
   * On screens wider than 1920 CSS-px the factor grows linearly so
   * text stays readable at 4K / TV resolutions.
   * Returns 1 for normal screens (≤ 1920px).
   */
  getFontScale(): number {
    // Determine the effective viewport width:
    // - DPR zoom active: body zoom = targetDpr/dpr, effective width = innerWidth * dpr/targetDpr
    //   (e.g., 3840x2400 at 250%: targetDpr=1.5, innerWidth=1536, effective=1536*2.5/1.5=2560)
    // - No DPR zoom: use the larger of screen.width and innerWidth
    //   (screen.width works on real TVs, innerWidth works in DevTools)
    const dpr = window.devicePixelRatio || 1;
    const targetDpr = this.getTargetDpr();
    const w = this.isDprZoomActive()
      ? Math.round((window.innerWidth || 0) * dpr / targetDpr)
      : Math.max(window.screen.width || 0, window.innerWidth || 0);
    if (w <= 1920) return 1;
    // Linear scale: 1 at 1920, 2 at 3840, capped at 3
    return Math.min(3, w / 1920);
  }

  private updateCSSVariables(): void {
    const vh = this.getEffectiveVh();
    const root = document.documentElement;
    const screen = this.getScreenCategory();

    // Font scale for large / TV screens.
    // When DPR zoom is active, body has zoom:targetDpr/dpr which shrinks everything,
    // so multiply fontScale by dpr/targetDpr to compensate for that shrinkage.
    const rawFontScale = this.getFontScale();
    const dprZoom = this.isDprZoomActive();
    const dpr = window.devicePixelRatio || 1;
    const targetDpr = this.getTargetDpr();
    const dprCompensation = dpr / targetDpr; // e.g., 2.5/1.5=1.67 for ultra-high-res
    const fontScale = (dprZoom && dprCompensation !== 1 && rawFontScale > 1)
      ? rawFontScale * dprCompensation
      : (dprZoom && dprCompensation !== 1) ? dprCompensation : rawFontScale;
    root.style.setProperty('--dvm-font-scale', fontScale.toString());

    // Re-scale ECharts fonts if scale changed (e.g., window moved between monitors)
    this.rescaleAllChartFonts();

    // Screen-size-aware overheads (smaller screens have less chrome)
    const overheads = {
      small:  { fixed: 120, row: 30, minChart: 180, minChartNoScroll: 120 },
      medium: { fixed: 150, row: 45, minChart: 200, minChartNoScroll: 100 },
      large:  { fixed: 180, row: 60, minChart: 200, minChartNoScroll: 80 },
    }[screen];

    // On small/medium screens, force scroll mode — can't fit multiple charts
    const effectiveNoScroll = (screen === 'small' || screen === 'medium') ? false : !this._screenFloating;

    // Update class on root for CSS to know the mode
    if (effectiveNoScroll) {
      root.classList.add('dvm-no-scroll');
    } else {
      root.classList.remove('dvm-no-scroll');
    }

    if (effectiveNoScroll && this.rowCount > 0) {
      // No-scroll: fit everything in viewport — divide by rowCount
      const noScrollOverheads = {
        small:  { fixed: 120, row: 30 },
        medium: { fixed: 140, row: 40 },
        large:  { fixed: 190, row: 54 },
      }[screen];

      const available = vh - noScrollOverheads.fixed - (this.rowCount * noScrollOverheads.row);
      const chartHeight = Math.max(overheads.minChartNoScroll, Math.round(available / this.rowCount));
      root.style.setProperty('--dvm-chart-height', `${chartHeight}px`);
      root.style.setProperty('--dvm-chart-height-md', `${Math.round(chartHeight * 1.1)}px`);
      root.style.setProperty('--dvm-chart-height-lg', `${Math.round(chartHeight * 1.25)}px`);
      root.style.setProperty('--dvm-chart-height-xl', `${Math.round(chartHeight * 1.5)}px`);
    } else {
      // Scroll mode: each chart gets a comfortable % of viewport height.
      // NOT divided by rowCount — users can scroll, so charts don't need to
      // squeeze into one screen.
      const factor = screen === 'small' ? 0.45 : screen === 'medium' ? 0.40 : 0.35;
      const chartHeight = Math.max(overheads.minChart, Math.round((vh - overheads.fixed) * factor));
      root.style.setProperty('--dvm-chart-height', `${chartHeight}px`);

      const chartHeightMd = Math.max(overheads.minChart + 50, Math.round((vh - overheads.fixed) * (factor + 0.07)));
      root.style.setProperty('--dvm-chart-height-md', `${chartHeightMd}px`);

      const chartHeightLg = Math.max(overheads.minChart + 100, Math.round((vh - overheads.fixed) * (factor + 0.15)));
      root.style.setProperty('--dvm-chart-height-lg', `${chartHeightLg}px`);

      // XL: full-panel widgets (entity table/form, gantt) — ~70% of viewport.
      // At 1920x1080: ~630px. At 1366x768: ~450px. At 4K: ~1500px.
      const chartHeightXl = Math.max(overheads.minChart + 250, Math.round((vh - overheads.fixed) * (factor + 0.35)));
      root.style.setProperty('--dvm-chart-height-xl', `${chartHeightXl}px`);
    }
  }

  /**
   * Dashboard-level auto-resize AND responsive grid for ALL ECharts instances.
   *
   * 1. ResizeObserver: auto-resizes charts when container dimensions change
   *    (initial flex layout, window resize, sidebar toggle).
   * 2. Grid scan: periodically detects new ECharts instances and applies
   *    responsive percentage-based grid config (containLabel: true) to
   *    cartesian charts. This prevents fixed-pixel grid padding from
   *    consuming chart area in compact containers.
   *
   * No per-component code needed — this handles everything from the dashboard.
   */
  startChartAutoResize(containerEl?: HTMLElement): void {
    const root = containerEl || document.body;

    this.chartResizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const chart = echarts.getInstanceByDom(entry.target as HTMLElement);
        chart?.resize();
      }
    });

    // Observe any existing chart containers
    this.observeChartContainers(root);

    // Watch for dynamically added chart containers (lazy-loaded components)
    this.chartMutationObserver = new MutationObserver(() => {
      this.observeChartContainers(root);
      this.applyResponsiveGridToAll(root);
    });
    this.chartMutationObserver.observe(root, { childList: true, subtree: true });

    // Periodically scan for new ECharts instances that need responsive grid.
    // Components call echarts.init() + setOption() asynchronously (after API calls),
    // so we poll until all charts are processed, then stop.
    this.gridScanTimer = setInterval(() => {
      this.applyResponsiveGridToAll(root);
    }, 500);
  }

  private observeChartContainers(root: HTMLElement): void {
    root.querySelectorAll('.card-body > div[id]').forEach((el) => {
      if (!this.observedElements.has(el)) {
        this.observedElements.add(el);
        this.chartResizeObserver?.observe(el);
      }
    });
  }

  /**
   * Find all ECharts instances in the dashboard and apply:
   * 1. Font scaling — DPR compensation or large/TV screen scaling
   *    (re-applied when a chart rebuilds its option via setOption)
   * 2. Responsive grid defaults for cartesian charts (one-time)
   */
  private applyResponsiveGridToAll(root: HTMLElement): void {
    const dprZoomActive = this.isDprZoomActive();
    const dpr = window.devicePixelRatio || 1;
    const targetDpr = this.getTargetDpr();

    // Compute effective font scale once per scan
    const rawFS = this.getFontScale();
    const dprFactor = (dprZoomActive && dpr !== 1) ? dpr / targetDpr : 1;
    const effectiveScale = (dprFactor > 1 && rawFS > 1)
      ? dprFactor * rawFS    // both: multiply to compensate body zoom + large screen
      : Math.max(dprFactor, rawFS); // one or neither

    root.querySelectorAll('.card-body > div[id]').forEach((el) => {
      const chart = echarts.getInstanceByDom(el as HTMLElement);
      if (!chart) return;

      const option = chart.getOption() as any;
      if (!option) return;

      // --- Font scaling (re-applied when chart rebuilds) ---
      if (effectiveScale > 1) {
        const currentFingerprint = this.getFontFingerprint(option);
        const storedFingerprint = this.fontFingerprints.get(el);

        // Apply scaling when:
        // - chart was never scaled (no fingerprint stored)
        // - chart was rebuilt and fonts reset to unscaled defaults
        //   (current fingerprint is significantly smaller than stored)
        if (storedFingerprint == null || currentFingerprint < storedFingerprint * 0.8) {
          this.scaleFontSizes(chart, option, effectiveScale);
          this.fontScaleMap.set(el, effectiveScale);
          // Read post-scaling font sizes as the new fingerprint
          const scaledOption = chart.getOption() as any;
          this.fontFingerprints.set(el, this.getFontFingerprint(scaledOption));
        }
      }
      this.lastEffectiveScale = effectiveScale;

      // --- Grid (one-time per element) ---
      if (this.gridAppliedElements.has(el)) return;

      // Only apply grid to cartesian charts (have xAxis or yAxis)
      const hasCartesianAxis = option.xAxis?.length > 0 || option.yAxis?.length > 0;
      if (!hasCartesianAxis) {
        this.gridAppliedElements.add(el);
        return;
      }

      // Apply responsive percentage-based grid with containLabel.
      const existingGrid = option.grid?.[0] || {};
      const parsePct = (v: any, fallback: number) => {
        if (typeof v === 'string' && v.endsWith('%')) return parseFloat(v) || fallback;
        return fallback;
      };
      chart.setOption({
        grid: {
          top: Math.max(parsePct(existingGrid.top, 0), 10) + '%',
          left: Math.max(parsePct(existingGrid.left, 0), 2) + '%',
          right: Math.max(parsePct(existingGrid.right, 0), 4) + '%',
          bottom: Math.max(parsePct(existingGrid.bottom, 0), 5) + '%',
          containLabel: true,
        },
      });

      this.gridAppliedElements.add(el);
    });
  }

  /**
   * Get a representative font size from a chart option.
   * Used to detect when a chart's option has been replaced (fonts reset).
   */
  private getFontFingerprint(option: any): number {
    return option.xAxis?.[0]?.axisLabel?.fontSize
      || option.yAxis?.[0]?.axisLabel?.fontSize
      || option.legend?.[0]?.textStyle?.fontSize
      || option.series?.[0]?.label?.fontSize
      || 0;
  }

  /**
   * Re-scale ECharts fonts on all already-processed charts when
   * the effective font scale changes (e.g., window dragged between monitors).
   * Uses a correction factor (newScale / oldScale) to avoid compounding.
   */
  private rescaleAllChartFonts(): void {
    const dprZoomActive = this.isDprZoomActive();
    const dpr = window.devicePixelRatio || 1;
    const targetDpr = this.getTargetDpr();
    const rawFS = this.getFontScale();
    const dprFactor = (dprZoomActive && dpr !== 1) ? dpr / targetDpr : 1;
    const newScale = (dprFactor > 1 && rawFS > 1)
      ? dprFactor * rawFS
      : Math.max(dprFactor, rawFS);

    if (Math.abs(newScale - this.lastEffectiveScale) < 0.01) return;

    this.fontScaleMap.forEach((oldScale, el) => {
      const chart = echarts.getInstanceByDom(el as HTMLElement);
      if (!chart) {
        this.fontScaleMap.delete(el);
        this.fontFingerprints.delete(el);
        return;
      }
      const option = chart.getOption() as any;
      if (!option) return;

      // Correction factor: undo old scale, apply new scale
      const correction = newScale / oldScale;
      this.scaleFontSizes(chart, option, correction);
      this.fontScaleMap.set(el, newScale);
      // Update fingerprint after rescaling
      const scaledOption = chart.getOption() as any;
      this.fontFingerprints.set(el, this.getFontFingerprint(scaledOption));
    });

    // Charts that were never scaled (oldScale=1) also need scaling if newScale > 1
    if (newScale > 1 && this.lastEffectiveScale <= 1) {
      document.querySelectorAll('.card-body > div[id]').forEach((el) => {
        if (this.fontScaleMap.has(el)) return; // already handled above
        const chart = echarts.getInstanceByDom(el as HTMLElement);
        if (!chart) return;
        const option = chart.getOption() as any;
        if (!option) return;
        this.scaleFontSizes(chart, option, newScale);
        this.fontScaleMap.set(el, newScale);
      });
    }

    this.lastEffectiveScale = newScale;
  }

  /**
   * Scale all ECharts text font sizes by the given factor.
   */
  private scaleFontSizes(chart: echarts.ECharts, option: any, dpr: number): void {
    const ECHARTS_DEFAULT_FONT = 10;

    const scale = (size: any, fallback = ECHARTS_DEFAULT_FONT): number => {
      const px = typeof size === 'number' ? size : fallback;
      return Math.round(px * dpr);
    };

    const scaleTextStyle = (obj: any, fallback = ECHARTS_DEFAULT_FONT): any => {
      if (!obj) return { fontSize: scale(undefined, fallback) };
      const result: any = { fontSize: scale(obj.fontSize, fallback) };
      if (obj.rich) {
        result.rich = {};
        for (const key of Object.keys(obj.rich)) {
          result.rich[key] = { fontSize: scale(obj.rich[key]?.fontSize, fallback) };
        }
      }
      return result;
    };

    const patch: any = {};

    if (option.title?.length) {
      patch.title = option.title.map((t: any) => {
        const p: any = {};
        p.textStyle = scaleTextStyle(t.textStyle, 18);
        if (t.subtextStyle?.fontSize != null) p.subtextStyle = scaleTextStyle(t.subtextStyle);
        return p;
      });
    }

    if (option.legend?.length) {
      patch.legend = option.legend.map((l: any) => ({
        textStyle: scaleTextStyle(l.textStyle),
      }));
    }

    if (option.tooltip?.length) {
      patch.tooltip = option.tooltip.map((t: any) => ({
        textStyle: scaleTextStyle(t.textStyle, 12),
      }));
    }

    if (option.xAxis?.length) {
      patch.xAxis = option.xAxis.map((axis: any) => {
        const p: any = { axisLabel: scaleTextStyle(axis.axisLabel) };
        if (axis.nameTextStyle?.fontSize != null) p.nameTextStyle = scaleTextStyle(axis.nameTextStyle);
        return p;
      });
    }

    if (option.yAxis?.length) {
      patch.yAxis = option.yAxis.map((axis: any) => {
        const p: any = { axisLabel: scaleTextStyle(axis.axisLabel) };
        if (axis.nameTextStyle?.fontSize != null) p.nameTextStyle = scaleTextStyle(axis.nameTextStyle);
        return p;
      });
    }

    if (option.series?.length) {
      patch.series = option.series.map((s: any) => {
        const seriesPatch: any = {};

        // Always scale series label (pie/donut/bar/line all have labels)
        // ECharts default label fontSize is 12
        seriesPatch.label = scaleTextStyle(s.label, 12);

        // Emphasis label (shown on hover — important for pie/donut)
        if (s.emphasis?.label) {
          seriesPatch.emphasis = { label: scaleTextStyle(s.emphasis.label, 12) };
        }

        // Treemap-specific
        if (s.upperLabel?.fontSize != null) seriesPatch.upperLabel = scaleTextStyle(s.upperLabel);
        if (s.levels?.length) {
          seriesPatch.levels = s.levels.map((level: any) => {
            const lp: any = {};
            if (level.label?.fontSize != null) lp.label = scaleTextStyle(level.label);
            if (level.upperLabel?.fontSize != null) lp.upperLabel = scaleTextStyle(level.upperLabel);
            return lp;
          });
        }
        return seriesPatch;
      });
    }

    chart.setOption(patch);
  }

  stopChartAutoResize(): void {
    if (this.gridScanTimer) {
      clearInterval(this.gridScanTimer);
      this.gridScanTimer = undefined;
    }
    this.chartResizeObserver?.disconnect();
    this.chartMutationObserver?.disconnect();
    this.chartResizeObserver = undefined;
    this.chartMutationObserver = undefined;
  }

  destroy(): void {
    this.stopChartAutoResize();
    this.resizeSub?.unsubscribe();
    this.fontScaleMap.clear();
    this.fontFingerprints.clear();
    this.lastEffectiveScale = 1;
    document.documentElement.classList.remove('dvm-no-scroll');
    this._screenFloating = true;
    this.initialized = false;
  }
}
